import { Badge, Button, EmptyState, Skeleton } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { isApiError } from "../../api/client";
import { cancelImport, getImport, type ImportJob } from "../../api/imports";
import { useServices } from "../../app/services";
import { ConfirmDrawer, Failure, Page } from "../settings/kit";
import { section } from "../settings/sections";
import s from "../settings/settings.module.css";
import { CutoverStep, DoneStep, RehearseStep, SignOffStep } from "./FinishSteps";
import { MappingStep } from "./MappingStep";
import o from "./onboarding.module.css";
import { CheckStep, ReviewStep } from "./ReviewStep";
import { StepError, useImportScope } from "./shared";
import { STEPS, canCancel, stageOf, stepIndex, type Stage } from "./state";
import { t } from "./strings";
import { UploadStep } from "./UploadStep";

/** `/settings/import/:id`. The step is derived from the server's job, so a reload lands on the same step. */
export function WizardPage() {
  const { id = "" } = useParams();
  const { client } = useServices();
  const sc = useImportScope(id);
  const canEdit = sc.ctx.has("settings.imports_edit");
  // `view` is only a detour (back to Sheets/Columns); it is lost on reload, and the server's state wins.
  const [view, setView] = useState<Stage | null>(null);
  const [askCancel, setAskCancel] = useState(false);
  const job = useQuery({
    queryKey: sc.jobKey,
    queryFn: () => getImport(client, id),
    retry: false,
    // Cutover runs on a queue: follow it until it stops.
    refetchInterval: (q) => (q.state.data?.status === "cutting_over" ? 3000 : false),
  });
  const cancel = useMutation({ mutationFn: () => cancelImport(client, id), onSuccess: (j) => { sc.putJob(j); setAskCancel(false); setView(null); } });
  const reload = () => { setView(null); void job.refetch(); };

  return (
    <Page title={job.data?.label ?? t.title} description={t.intro} allow={section("import")}
      back={false}
      actions={job.data && canEdit && canCancel(job.data.status) ? <Button onClick={() => setAskCancel(true)}>{t.cancel.button}</Button> : undefined}>
      <Link to="/settings/import" className={`${s.crumb} ${o.inset}`}>← {t.listTitle}</Link>
      {job.isPending ? <div className={s.skel} aria-busy="true" aria-label="Loading import"><Skeleton height={12} /><Skeleton height={120} /><Skeleton height={34} /></div> : null}
      {job.error ? (isApiError(job.error) && job.error.status === 404
        ? <EmptyState compact icon="alert" title={t.notFound.title} description={t.notFound.body} action={<Button as={Link} to="/settings/import">{t.listTitle}</Button>} />
        : <Failure error={job.error} what="this import" onRetry={() => void job.refetch()} />) : null}
      {job.data ? <Body job={job.data} view={view} setView={setView} reload={reload} canEdit={canEdit} /> : null}
      <ConfirmDrawer open={askCancel} onClose={() => setAskCancel(false)} title={t.cancel.title} description={t.cancel.body} confirmLabel={t.cancel.confirm} busy={cancel.isPending} onConfirm={() => cancel.mutate()}>
        <StepError error={cancel.error} onReload={() => { setAskCancel(false); reload(); }} />
      </ConfirmDrawer>
    </Page>
  );
}

function Body({ job, view, setView, reload, canEdit }: { job: ImportJob; view: Stage | null; setView: (s: Stage | null) => void; reload: () => void; canEdit: boolean }) {
  const server = stageOf(job);
  // A detour is only honoured while the job can still be edited; once cutover starts the server's stage is the only one.
  const stage = view && canCancel(job.status) ? view : server;
  const at = stepIndex(stage);
  const edit = (to: "upload" | "mapping") => setView(to);
  return (
    <>
      <ol className={o.stepper} aria-label="Import steps">
        {STEPS.map((st, i) => (
          <li key={st.key} className={o.step} data-state={stage === "cancelled" ? "todo" : i < at ? "done" : i === at ? "current" : "todo"} aria-current={i === at ? "step" : undefined}>
            <span>{i + 1}. {st.label}</span>
          </li>
        ))}
      </ol>
      {!canEdit && stage !== "done" && stage !== "cancelled" ? <div className={`${s.note} ${o.inset}`} style={{ margin: 0 }}>You can look at this import but not change it. Ask the owner for access.</div> : null}
      <fieldset disabled={!canEdit && stage !== "done"} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        {stage === "upload" ? <UploadStep job={job} onReload={reload} onUploaded={() => setView("upload")} onContinue={() => setView(null)} /> : null}
        {stage === "mapping" ? <MappingStep job={job} onReload={reload} onDone={() => setView(null)} onAddSheet={() => setView("upload")} /> : null}
        {stage === "check" ? <CheckStep job={job} onReload={reload} onBack={() => setView("mapping")} /> : null}
        {stage === "review" ? <ReviewStep job={job} onReload={reload} onEdit={edit} /> : null}
        {stage === "rehearse" ? <RehearseStep job={job} onReload={reload} onEdit={edit} /> : null}
        {stage === "signoff" ? <SignOffStep job={job} onReload={reload} /> : null}
        {stage === "cutover" ? <CutoverStep job={job} onReload={reload} /> : null}
        {stage === "done" ? <DoneStep job={job} /> : null}
        {stage === "cancelled" ? <EmptyState compact icon="alert" title={t.cancelled.title} description={t.cancelled.body} action={<Button as={Link} to="/settings/import">{t.listTitle}</Button>} /> : null}
      </fieldset>
      <StatusLine job={job} />
    </>
  );
}

function StatusLine({ job }: { job: ImportJob }) {
  return <div className={`${s.hint} ${o.inset}`}>Status <Badge tone="neutral">{job.status.replace(/_/g, " ")}</Badge> · version {job.version}</div>;
}
